<div class="p-6">
  <div class="space-y-6">
    <!-- ID and Topic Type in two columns -->
    <div class="grid grid-cols-2 gap-6">
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">ID</label>
        <button
          [dojoCopyToClipboard]="data().id"
          class="group inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm text-gray-500 hover:text-gray-700"
        >
          {{ data().id }}
          <dojo-icon icon="copy" [size]="'xs'" class="opacity-40 group-hover:opacity-100"></dojo-icon>
        </button>
      </div>
      @if (effectiveTopicType(); as topicInfo) {
        <div>
          <label class="mb-1 block text-sm font-semibold text-gray-700">Topic Type</label>
          <div>
            <button
              [matMenuTriggerFor]="topicTypeMenu"
              class="inline-flex cursor-pointer items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80"
              [style.background-color]="effectiveTopicTypeColor()"
            >
              {{ topicInfo.value | titlecase }}
              <dojo-icon icon="circle-info" [size]="'xs'" class="ml-1 opacity-60"></dojo-icon>
            </button>
            <mat-menu #topicTypeMenu="matMenu" class="no-width-constraints">
              <div class="w-[560px] p-4" (click)="$event.stopPropagation()">
                <div class="mb-3 text-sm font-semibold text-gray-900">Topic Types</div>
                <table class="w-full text-left text-xs">
                  <tbody>
                    @for (type of topicTypes; track type.name) {
                      <tr class="border-b border-gray-100 last:border-0">
                        <td class="py-2 pr-3 align-top">
                          <div class="flex items-center gap-1.5">
                            <span
                              class="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium"
                              [style.background-color]="type.color"
                            >
                              {{ type.name | titlecase }}
                            </span>
                            @if (type.name === topicInfo.value) {
                              <dojo-icon icon="circle-check" [size]="'xs'" class="text-green-500"></dojo-icon>
                            }
                          </div>
                        </td>
                        <td class="py-2 align-top">
                          <div class="text-gray-700">{{ type.description }}</div>
                          <div class="mt-0.5 text-gray-400">{{ type.topics }}</div>
                          <div class="mt-1 rounded bg-gray-100 px-2 py-1 font-mono text-gray-600">
                            <markdown [data]="type.example" [katex]="true" [katexOptions]="katexOptions"></markdown>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </mat-menu>
            @if (topicInfo.inheritedFrom) {
              <div class="mt-1 text-sm text-gray-400">
                (inherited from
                <button
                  (click)="navigateToNode.emit(topicInfo.inheritedFrom.id)"
                  class="text-gray-500 underline hover:text-gray-700"
                >
                  {{ topicInfo.inheritedFrom.name }}
                </button>
                )
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Name -->
    <div>
      <label class="mb-1 block text-sm font-semibold text-gray-700">Name</label>
      <div class="flex items-center gap-2">
        @if (data().icon) {
          <dojo-icon [icon]="data().icon!" [size]="'m'" class="text-gray-500"></dojo-icon>
        }
        <span class="font-mono text-base text-gray-500">{{ data().name }}</span>
      </div>
    </div>

    @if (data().description) {
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">Description</label>
        <div class="text-base text-gray-500">
          <markdown [data]="data().description!" [katex]="true"></markdown>
        </div>
      </div>
    }

    @if (prerequisiteNames().length > 0) {
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">Prerequisites</label>
        <div class="flex flex-wrap gap-2">
          @for (prereq of prerequisiteNames(); track prereq.id) {
            <button
              (click)="navigateToNode.emit(prereq.id)"
              class="group inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm text-gray-700 hover:bg-gray-200"
            >
              {{ prereq.name }}
              <dojo-icon
                icon="arrow-up-right-from-square"
                [size]="'xs'"
                class="text-gray-400 group-hover:text-gray-600"
              ></dojo-icon>
            </button>
          }
        </div>
      </div>
    }

    @if (data().parent_id) {
      <div>
        <label class="mb-1 block text-sm font-semibold text-gray-700">Parent</label>
        <button
          (click)="navigateToNode.emit(data().parent_id!)"
          class="group inline-flex items-center gap-2 text-base text-gray-700 hover:text-gray-900"
        >
          {{ parentName() }}
          <dojo-icon
            icon="arrow-up-right-from-square"
            [size]="'xs'"
            class="text-gray-400 group-hover:text-gray-600"
          ></dojo-icon>
        </button>
      </div>
    }

    <!-- Verification Section -->
    <div>
      <label class="mb-2 block text-sm font-semibold text-gray-700">Verification</label>
      @if (verificationReviews().length > 0) {
        <div class="space-y-2">
          @for (review of verificationReviews(); track review.reviewer + review.date) {
            <div
              class="flex items-center justify-between rounded-lg border px-3 py-2"
              [class.border-green-200]="review.status === 'verified'"
              [class.bg-green-50]="review.status === 'verified'"
              [class.border-amber-200]="review.status === 'in_progress'"
              [class.bg-amber-50]="review.status === 'in_progress'"
            >
              <div class="flex items-center gap-2">
                <dojo-icon
                  [icon]="review.status === 'verified' ? 'circle-check' : 'clock'"
                  [size]="'s'"
                  [class.text-green-600]="review.status === 'verified'"
                  [class.text-amber-600]="review.status === 'in_progress'"
                ></dojo-icon>
                <span class="font-medium text-gray-700">{{ review.reviewer }}</span>
              </div>
              <div class="flex items-center gap-3">
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-green-100]="review.status === 'verified'"
                  [class.text-green-700]="review.status === 'verified'"
                  [class.bg-amber-100]="review.status === 'in_progress'"
                  [class.text-amber-700]="review.status === 'in_progress'"
                >
                  {{ review.statusLabel }}
                </span>
                <span class="text-xs text-gray-500">{{ review.date | date: 'mediumDate' }}</span>
              </div>
            </div>
          }
          <!-- Show "one more reviewer missing" if less than 2 reviewers -->
          @if (verificationReviews().length < 2) {
            <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-center">
              <p class="text-sm text-gray-500">One more reviewer missing</p>
            </div>
          }
        </div>
      } @else {
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center">
          <dojo-icon icon="user-clock" [size]="'m'" class="mb-1 text-gray-400"></dojo-icon>
          <p class="text-sm text-gray-500">No reviews yet</p>
        </div>
      }
    </div>
  </div>
</div>
